import React, { useState, useEffect, useCallback } from 'react'

import {
  TextField,
  MenuItem,
  Stack,
  InputAdornment,
  useMediaQuery,
  Box,
  Select,
  Button,
  Tooltip,
} from '@mui/material'

import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useTheme } from '@emotion/react'
import { useNavigate } from 'react-router-dom'

import { defaultFunction, getVisitDestination } from '../../utilities/utilities'
import MiniIsotype from '../Svgs/MiniIsoType'
import { Types } from '../../../state/actionTypes'
import { _dispatch as dispatch, defaultSuccess, defaultCatch, setLoading, setSnackbar } from '../../../state/actions'

import './VisitorsFilter.sass'
import { api } from '../../../api/api'
import AutocompleteVirtualized from '../AutocompleteVirtualized/AutocompleteVirtualized'
import db, { UNSYNCED_VISIT_STATUSES } from '../../../db/db'
import { syncVisitsForBuilding } from '../../../sync/SyncEngine'

const ALL_DESTINATIONS_ID = '__all__'

function VisitorsFilter(props) {
  const {
    setLoading,
    dispatch,
    defaultSuccess,
    defaultCatch,
    building_id = -1,
    buildings = [],
    prefix = '',
    setPrefix = defaultFunction,
    destinationIds,
    visitStatus,
    handleChangeDestination,
    handleChangeVisitStatus,
    handleFilterData,
    visitDestinations,
    isOnline = true,
  } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))

  const ALL_DESTINATIONS_OPTION = { id: ALL_DESTINATIONS_ID, name: t('all') }
  const destinationOptions = [ALL_DESTINATIONS_OPTION, ...getVisitDestination(visitDestinations)]
  const [hasUnsyncedVisits, setHasUnsyncedVisits] = useState(false)
  const [localPrefix, setLocalPrefix] = useState(prefix)

  useEffect(() => {
    setLocalPrefix(prefix)
  }, [prefix])

  const destinationDisplayValue = destinationIds.length === 0
    ? [ALL_DESTINATIONS_OPTION]
    : destinationIds

  const onChangeDestinationLocal = (event, newValue) => {
    const prevHadAll = destinationIds.length === 0
    const newHasAll  = newValue.some(v => v.id === ALL_DESTINATIONS_ID)
    const realItems  = newValue.filter(v => v.id !== ALL_DESTINATIONS_ID)

    if (newHasAll && !prevHadAll) {
      handleChangeDestination(event, [])
      return
    }

    if (realItems.length === 0) {
      handleChangeDestination(event, [])
      return
    }

    handleChangeDestination(event, realItems)
  }

  // Verificar si hay datos sin sincronizar: visitas `pending`/`failed` en
  // `db.visits`, o filas remanentes en `quarantined_visits` (p.ej. tras un
  // "Reset DB" desde el Offline Audit Page sin haber cerrado sesión — esa
  // cuarentena solo se resuelve vía PendingVisitsModal en el próximo login,
  // así que mientras exista no debería poder cambiarse de edificio).
  const checkUnsyncedVisits = useCallback(async () => {
    const [unsyncedCount, quarantinedCount] = await Promise.all([
      db.visits.where('syncStatus').anyOf(UNSYNCED_VISIT_STATUSES).count(),
      db.quarantined_visits.count(),
    ])
    setHasUnsyncedVisits(unsyncedCount > 0 || quarantinedCount > 0)
  }, [])

  useEffect(() => {
    checkUnsyncedVisits()
    const handler = () => checkUnsyncedVisits()
    // Re-check after every sync outcome and whenever a visit is written as pending
    window.addEventListener('lobby:sync-complete', handler)
    window.addEventListener('lobby:sync-error', handler)
    window.addEventListener('lobby:visit-pending', handler)
    return () => {
      window.removeEventListener('lobby:sync-complete', handler)
      window.removeEventListener('lobby:sync-error', handler)
      window.removeEventListener('lobby:visit-pending', handler)
    }
  }, [checkUnsyncedVisits])

  const isBuildingChangeDisabled = !isOnline || hasUnsyncedVisits

  const getBuildingChangeTooltip = () => {
    if (!isOnline) return t('cannot_change_building_offline')
    if (hasUnsyncedVisits) return t('cannot_change_building_pending')
    return ''
  }

  const onChangeBuilding = async newBuildingId => {
    if (isBuildingChangeDisabled) return

    const payload = { user: { building_id: newBuildingId } }
    setLoading(true)
    try {
      const response = await api.post('/profile', payload)
      defaultSuccess(response)
      const { data } = response
      if (!data.success) return

      dispatch({ type: Types.SET_BUILDING, payload: newBuildingId })

      // Limpiar visitas del edificio anterior y re-sincronizar
      await syncVisitsForBuilding()
      window.dispatchEvent(new CustomEvent('lobby:sync-complete'))
    } catch (error) {
      defaultCatch(error, navigate)
    } finally {
      setLoading(false)
    }
  }

  const buildingSelector = (
    <TextField
      select
      fullWidth
      name="type"
      value={building_id}
      size="small"
      disabled={isBuildingChangeDisabled}
      onChange={e => onChangeBuilding(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <MiniIsotype width="30px" />
          </InputAdornment>
        ),
      }}
    >
      {buildings.map(b => (
        <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
      ))}
    </TextField>
  )

  return (
    <Stack className='VisitorsFilter' direction="column" spacing={4}>
      <Stack direction="row" spacing={3} alignItems="center">
        {isLarge && <h1>{t('Checked-in visits')}</h1>}
        <Stack
          className='VisitorsFilter__site-wrapper'
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {isLarge && <label>{t('You are viewing the records of')}:</label>}
          {isBuildingChangeDisabled ? (
            <Tooltip title={getBuildingChangeTooltip()}>
              <span style={{ width: '100%' }}>{buildingSelector}</span>
            </Tooltip>
          ) : buildingSelector}
        </Stack>
        <TextField
          value={localPrefix}
          placeholder={t('Search')}
          onChange={e => {
            const nextValue = e.target.value
            setLocalPrefix(nextValue)
            setPrefix(nextValue)
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <div className='VisitorsFilter__container'>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          className='VisitorsFilter__container__item'
        >
          <label>{t('heads_towards')}:</label>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AutocompleteVirtualized
              options={destinationOptions}
              getOptionLabel={(option) => option.name}
              value={destinationDisplayValue}
              onChange={onChangeDestinationLocal}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              disableCloseOnSelect
              multiple
            />
          </Box>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          className='VisitorsFilter__container__item'
        >
          <label>{t('visit_status')}:</label>
          <Select
            variant="outlined"
            size='small'
            value={visitStatus}
            onChange={handleChangeVisitStatus}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <MenuItem value="-1">{t('all')}</MenuItem>
            <MenuItem value="1">{t('check_visit_status')}</MenuItem>
            <MenuItem value="2">{t('no_check_visit_status')}</MenuItem>
          </Select>
        </Stack>
        <Box className='VisitorsFilter__container__item' display="flex" alignItems="center">
          <Button
            sx={{ maxWidth: '200px', width: '100%' }}
            variant='contained'
            onClick={handleFilterData}
          >
            {t('filter_data')}
          </Button>
        </Box>
      </div>
    </Stack>
  )
}

const mapStateToProps = state => ({
  buildings: state.profile.buildings,
  building_id: state.profile.building_id,
  visitDestinations: state.profile.visitDestinations,
  isOnline: state.utilities.isOnline,
})

const mapDispatchToProps = {
  dispatch,
  defaultSuccess,
  defaultCatch,
  setLoading,
  setSnackbar,
}

export default connect(mapStateToProps, mapDispatchToProps)(VisitorsFilter)
